import React from 'react';
import { Link } from '@inertiajs/react';
import { Button } from '../../components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/ui/table';
import { useTrans } from '../../hooks/useTrans';
import { trackAndTraceUrl } from '../../lib/routes';
import { formatMoney } from '../../lib/formatters';

function DetailRow({ label, children }) {
    return (
        <div className="flex gap-4 mb-4">
            <label className="w-40 shrink-0 text-sm font-bold text-[#003545]">{label}:</label>
            <div className="text-sm text-[#003545]">{children}</div>
        </div>
    );
}

function TaskSection({ task, order }) {
    const translate = useTrans();

    if (!task) return null;

    return (
        <div>
            <h3 className="text-xl font-bold text-[#003545] mb-4">{translate(task.type)}</h3>

            <DetailRow label={translate('Adres')}>
                <div>
                    {task.address.name}<br />
                    {task.address.street}<br />
                    {task.address.zipCity}
                </div>
                {task.timeWindow.date && (
                    <div className="mt-4">
                        {translate('Op')} <u>{task.timeWindow.date}</u> {translate('tussen')}{' '}
                        <u>{task.timeWindow.timeFrom} - {task.timeWindow.timeTo}</u>
                    </div>
                )}
            </DetailRow>

            {task.address.contactEmail && (
                <DetailRow label={translate('Contactpersoon')}>
                    {task.address.contactEmail}
                </DetailRow>
            )}

            <DetailRow label={translate('Opmerkingen')}>
                {task.instructions}
            </DetailRow>

            {order.showPersonalReference && order.personalReferenceLabel && task.mendrixDebtorReference && (
                <DetailRow label={order.personalReferenceLabel}>
                    {task.mendrixDebtorReference}
                </DetailRow>
            )}

            {order.showOurReference && order.ourReferenceLabel && task.mendrixOurReference && (
                <DetailRow label={order.ourReferenceLabel}>
                    {task.mendrixOurReference}
                </DetailRow>
            )}
        </div>
    );
}

function GoodsTable({ goods, route }) {
    const translate = useTrans();

    return (
        <Table>
            <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                <TableRow>
                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Type')}</TableHead>
                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Aantal')}</TableHead>
                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Gewicht')}</TableHead>
                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Afmetingen')}</TableHead>
                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Stapelbaar')}</TableHead>
                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Breekbaar')}</TableHead>
                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Productomschrijving')}</TableHead>
                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Barcode')}</TableHead>
                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Status')}</TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                {goods.map((good, index) => (
                    <TableRow key={good.id} className={index % 2 === 1 ? 'bg-[#f5f5f5]' : ''}>
                        <TableCell className="py-5 px-2.5">{good.type}</TableCell>
                        <TableCell className="py-5 px-2.5">{good.amount}</TableCell>
                        <TableCell className="py-5 px-2.5">{good.weight} kg</TableCell>
                        <TableCell className="py-5 px-2.5">{good.dimensions}</TableCell>
                        <TableCell className="py-5 px-2.5">{good.stackable ? translate('Ja') : translate('Nee')}</TableCell>
                        <TableCell className="py-5 px-2.5">{good.fragile ? translate('Ja') : translate('Nee')}</TableCell>
                        <TableCell className="py-5 px-2.5">{good.description}</TableCell>
                        <TableCell className="py-5 px-2.5">{good.barcode}</TableCell>
                        <TableCell className="py-5 px-2.5">
                            {good.scanStatus
                                ? `${translate(good.scanStatus.description)} — ${good.scanStatus.date}`
                                : route.delivery?.actualEnd
                                    ? translate('Afgeleverd')
                                    : route.pickup?.actualEnd
                                        ? translate('Opgehaald')
                                        : translate('Order aangemaakt')}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
}

function BackLink({ backUrl }) {
    const translate = useTrans();

    return (
        <Button variant="clear" asChild>
            <Link href={backUrl}>&#x3c; {translate('Terug naar orderoverzicht')}</Link>
        </Button>
    );
}

export default function OrdersShow({ orderId, order, transportRoutes, backUrl }) {
    const translate = useTrans();

    return (
        <div className="max-w-[1200px] mx-auto px-4 space-y-6">
            {/* Order Details Panel */}
            <div className="bg-white rounded-[3px] border border-[#bbb]">
                <div className="p-[30px]">
                    <h3 className="text-2xl font-bold text-[#003545] mb-6">{translate('Orderdetails')}</h3>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
                        <div>
                            <DetailRow label={translate('Orderdatum')}>{order.date}</DetailRow>
                            <DetailRow label={translate('Ordernummer')}>
                                {order.externalOrderId || <em>{translate('Onbekend')}</em>}
                            </DetailRow>
                            <DetailRow label={translate('Status')}>
                                {order.isRetour && (
                                    <span className="text-red-600 mr-1">
                                        <svg className="inline h-4 w-4 fill-current" role="img">
                                            <use href="#glyph-retour"></use>
                                        </svg>
                                        {' '}{translate('Retour')}{' '}
                                    </span>
                                )}
                                {order.status}
                            </DetailRow>
                            <DetailRow label={translate('Aangemaakt')}>{order.dateTime}</DetailRow>
                        </div>
                        <div>
                            <DetailRow label={translate('Debiteur')}>{order.debtor}</DetailRow>
                            <DetailRow label={translate('Transporttype')}>{order.transportType}</DetailRow>
                            <DetailRow label={order.labelReference || translate('Referentie')}>{order.reference}</DetailRow>
                            <DetailRow label={translate('Contactpersoon')}>{order.contact}</DetailRow>
                            <DetailRow label={order.labelCustomerReference || translate('Klantreferentie')}>{order.orderReference}</DetailRow>
                            {order.shipmentValue != null && (
                                <DetailRow label={translate('Verzekerd bedrag')}>&euro; {formatMoney(order.shipmentValue)}</DetailRow>
                            )}
                        </div>
                    </div>
                </div>

                <div className="border-t border-[#bbb] px-[30px] py-4 flex items-center justify-between">
                    <BackLink backUrl={backUrl} />
                    {order.externalOrderId && (
                        <div className="flex gap-2">
                            <Button variant="primary" asChild>
                                <a href={`/order/${order.externalOrderId}/vrachtbrief`}>{translate('Download vrachtbrief')}</a>
                            </Button>
                            <Button variant="primary" asChild>
                                <a href={`/order/${order.externalOrderId}/etiket`}>{translate('Download etiket')}</a>
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Route Panels */}
            {transportRoutes.map((route, index) => (
                <div key={index} className="bg-white rounded-[3px] border border-[#bbb]">
                    <div className="p-[30px]">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-2xl font-bold text-[#003545]">
                                {translate('Rit')} {route.pickup?.address.name} - {route.delivery?.address.name}
                            </h3>
                            {route.delivery?.traceUuid && (
                                <Button variant="primary" asChild>
                                    <a href={trackAndTraceUrl(route.delivery.traceUuid, { goodId: route.goods[0]?.id, fromOrderId: orderId })}>
                                        {translate('Track & Trace')}
                                    </a>
                                </Button>
                            )}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <TaskSection task={route.pickup} order={order} />
                            <TaskSection task={route.delivery} order={order} />
                        </div>

                        <h3 className="text-xl font-bold text-[#003545] mt-6 mb-4">{translate('Goederen')}</h3>
                        {route.goods.length > 0 ? (
                            <div className="overflow-x-auto">
                                <GoodsTable goods={route.goods} route={route} />
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">{translate('Er zijn nog geen goederen toegevoegd voor deze rit.')}</p>
                        )}
                    </div>

                    <div className="border-t border-[#bbb] px-[30px] py-4">
                        <BackLink backUrl={backUrl} />
                    </div>
                </div>
            ))}

            {/* Status Mutations Panel */}
            {order.statusMutations.length > 0 && (
                <div id="status-mutations" className="bg-white rounded-[3px] border border-[#bbb]">
                    <div className="p-[30px]">
                        <h3 className="text-2xl font-bold text-[#003545] mb-6">{translate('Statushistorie')}</h3>

                        <Table>
                            <TableHeader className="bg-[#ededed] [&_tr]:border-b-2 [&_tr]:border-[#d4d4d4]">
                                <TableRow>
                                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Status')}</TableHead>
                                    <TableHead className="py-5 px-2.5 text-[#003545] font-normal text-sm">{translate('Tijdstip')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {order.statusMutations.map((mutation, index) => (
                                    <TableRow key={index} className={index % 2 === 1 ? 'bg-[#f5f5f5]' : ''}>
                                        <TableCell className="py-5 px-2.5">{mutation.status}</TableCell>
                                        <TableCell className="py-5 px-2.5">{mutation.createdAt}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="border-t border-[#bbb] px-[30px] py-4">
                        <BackLink backUrl={backUrl} />
                    </div>
                </div>
            )}
        </div>
    );
}
